import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Zap } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { api } from "../api";
import { useFactory } from "../store";
import type { LaneId } from "../types";
import { DEFAULT_REPO, RepoSelect } from "./issues/repo-select";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "./ui/field";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Spinner } from "./ui/spinner";
import { Textarea } from "./ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "./ui/toggle-group";

const DEFAULT = "default";
/** Route value for "let intake pick the lane". */
const INTAKE = "intake";

const EMPTY = {
  title: "",
  description: "",
  startAgentId: DEFAULT,
  route: INTAKE as LaneId,
  provider: DEFAULT,
  repoKey: DEFAULT_REPO,
  instructions: "",
};

export function CreateTaskDialog({ trigger }: { trigger?: ReactNode }) {
  const { layout, state } = useFactory();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const update = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));
  const create = useMutation({
    mutationFn: () =>
      api.createTask({
        title: form.title.trim(),
        description: form.description || undefined,
        startAgentId: form.startAgentId === DEFAULT ? undefined : form.startAgentId,
        lane: form.route === INTAKE ? undefined : form.route,
        provider: form.provider === DEFAULT ? undefined : form.provider,
        repoKey: form.repoKey === DEFAULT_REPO ? undefined : form.repoKey,
        instructions: form.instructions || undefined,
      }),
    onSuccess: ({ task }) => {
      toast.success("Work order started", { description: task.title });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["state"] });
      setOpen(false);
      setForm(EMPTY);
      navigate(`/tasks/${task.id}`);
    },
    onError: (error: Error) => toast.error("Could not start the work order", { description: error.message }),
  });
  const agents =
    layout?.sections.filter((s) => !s.detached).flatMap((s) => s.agents.filter((a) => a.kind === "llm")) ??
    [];
  const lanes = Object.entries(layout?.lanes ?? {});
  const routeLane = form.route === INTAKE ? undefined : layout?.lanes[form.route];
  const providers = Object.entries(state?.providers ?? {});

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button>
            <Plus /> New work order
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (form.title.trim()) create.mutate();
          }}
          className="grid gap-6"
        >
          <DialogHeader>
            <DialogTitle>New work order</DialogTitle>
            <DialogDescription>
              Send a job down the line by hand. Skynet issues arrive here automatically when intake picks them
              up.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="task-title">What needs building?</FieldLabel>
              <Input
                id="task-title"
                required
                autoFocus
                value={form.title}
                onChange={(e) => update({ title: e.target.value })}
                placeholder="Fix the checkout total when a discount is applied"
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="task-details">Details</FieldLabel>
              <Textarea
                id="task-details"
                rows={3}
                value={form.description}
                onChange={(e) => update({ description: e.target.value })}
                placeholder="Steps to reproduce, acceptance criteria, links…"
              />
            </Field>
            <Field>
              <FieldLabel>Route</FieldLabel>
              <ToggleGroup
                type="single"
                variant="outline"
                value={form.route}
                onValueChange={(v) => v && update({ route: v as LaneId })}
                className="w-full flex-wrap"
              >
                <ToggleGroupItem value={INTAKE} className="flex-1">
                  Through intake
                </ToggleGroupItem>
                {lanes.map(([id, lane]) => (
                  <ToggleGroupItem key={id} value={id} className="flex-1">
                    {id === "hotfix" && <Zap />} {lane.name}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
              <FieldDescription>
                {routeLane
                  ? `Skips intake. ${routeLane.description}`
                  : "Evaluated, estimated and endorsed first; the endorser picks the lane."}
              </FieldDescription>
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field>
                <FieldLabel>Start at station</FieldLabel>
                <Select value={form.startAgentId} onValueChange={(v) => update({ startAgentId: v })}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={DEFAULT}>
                      {routeLane ? "First lane station" : "First intake station"}
                    </SelectItem>
                    {agents.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field>
                <FieldLabel>Provider</FieldLabel>
                <Select value={form.provider} onValueChange={(v) => update({ provider: v })}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={DEFAULT}>Per station</SelectItem>
                    {providers.map(([id, p]) => (
                      <SelectItem key={id} value={id} disabled={!p.configured}>
                        {id}
                        {p.configured ? "" : " (not configured)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field>
              <FieldLabel htmlFor="task-repo">Repository</FieldLabel>
              <RepoSelect
                id="task-repo"
                value={form.repoKey}
                onChange={(repoKey) => update({ repoKey })}
                allowDefault
                className="w-full"
              />
              <FieldDescription>
                The repo the stations clone and open the pull request against.
              </FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor="task-instructions">Operator instructions</FieldLabel>
              <Input
                id="task-instructions"
                value={form.instructions}
                onChange={(e) => update({ instructions: e.target.value })}
                placeholder="Keep the change behind a feature flag"
              />
              <FieldDescription>Appended to every prompt for this work order.</FieldDescription>
            </Field>
          </FieldGroup>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || !form.title.trim()}>
              {create.isPending && <Spinner />}
              Start production
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
